import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Typography, Tooltip } from "@material-ui/core";
import styled, { keyframes } from "styled-components";
import apiFetch from "../util/fetch-utils";
import WorkspaceHeader from "../common/WorkspaceHeader";

/* ── Animations ───────────────────────────────────────────── */
const fadeIn = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; }`;

/* ── Page layout ──────────────────────────────────────────── */
const Page = styled.div`
    width: 100%;
    animation: ${fadeIn} 200ms ease;
`;

const Section = styled.div`
    margin-top: 20px;
`;

/* ── Stat tiles ───────────────────────────────────────────── */
const TilesRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;

const Tile = styled.div`
    border-radius: 14px;
    background: ${({ $gradient }) => $gradient};
    box-shadow: 0 4px 16px ${({ $shadow }) => $shadow};
    padding: 18px 20px;
    height: 100px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const TileLabel = styled.div`
    color: rgba(255,255,255,0.82);
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    font-family: "Inter", system-ui, sans-serif;
`;

const TileNumber = styled.div`
    color: #fff;
    font-weight: 800;
    font-size: 2rem;
    line-height: 1;
    letter-spacing: -1px;
    font-variant-numeric: tabular-nums;
    font-family: "Inter", system-ui, sans-serif;
`;

const TileSub = styled.div`
    color: rgba(255,255,255,0.6);
    font-size: 0.68rem;
    font-family: "Inter", system-ui, sans-serif;
`;

/* ── Filter bar ───────────────────────────────────────────── */
const FilterBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 12px;
`;

const FilterBtn = styled.button`
    appearance: none;
    border: 1px solid ${({ $active }) => $active ? "#6366f1" : "#e5e7eb"};
    background: ${({ $active }) => $active ? "#ede9fe" : "#fff"};
    color: ${({ $active }) => $active ? "#4f46e5" : "#374151"};
    border-radius: 99px;
    padding: 5px 14px;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 120ms ease;
    &:hover { border-color: #c7d2fe; background: #f5f3ff; color: #4f46e5; }
`;

/* ── Table ────────────────────────────────────────────────── */
const TableWrap = styled.div`
    border-radius: 14px;
    border: 1px solid #f0f0f0;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0,0,0,.06);
    background: #fff;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
`;

const THead = styled.thead`
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
`;

const Th = styled.th`
    padding: 10px 16px;
    font-size: 0.72rem;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: left;
    white-space: nowrap;
`;

const Tr = styled.tr`
    border-bottom: 1px solid #f9fafb;
    transition: background 100ms ease;
    &:last-child { border-bottom: none; }
    &:hover { background: #f8f7ff; }
`;

const ExpandBtn = styled.button`
    appearance: none;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #6b7280;
    border-radius: 6px;
    padding: 3px 8px;
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: all 120ms ease;
    &:hover { background: #f5f3ff; border-color: #c7d2fe; color: #4f46e5; }
`;

const Td = styled.td`
    padding: 10px 16px;
    font-size: 0.85rem;
    color: #374151;
    overflow: hidden;
    vertical-align: middle;
`;

/* ── Chain visualization ──────────────────────────────────── */
const ChainRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
`;

const HopBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 7px;
    border-radius: 6px;
    font-size: 0.68rem;
    font-weight: 700;
    white-space: nowrap;
    background: ${({ $bg }) => $bg};
    color: ${({ $color }) => $color};
`;

const Arrow = styled.span`
    color: #d1d5db;
    font-size: 0.75rem;
    flex-shrink: 0;
`;

/* ── Issue badges ─────────────────────────────────────────── */
const IssueBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 7px;
    border-radius: 6px;
    font-size: 0.68rem;
    font-weight: 700;
    margin-right: 4px;
    background: ${({ $bg }) => $bg};
    color: ${({ $color }) => $color};
`;

/* ── URL text ─────────────────────────────────────────────── */
const UrlLink = styled.a`
    color: #4f46e5;
    text-decoration: none;
    font-weight: 500;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &:hover { text-decoration: underline; }
`;

const SubText = styled.span`
    display: block;
    font-size: 0.72rem;
    color: #9ca3af;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 2px;
`;

/* ── Expanded detail row ──────────────────────────────────── */
const DetailRow = styled.tr`
    background: #f8f7ff;
    border-bottom: 1px solid #e5e7eb;
`;

const DetailTd = styled.td`
    padding: 12px 20px 16px 20px;
`;

const ChainCard = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
`;

const StepRow = styled.div`
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr) 54px minmax(0, 1fr);
    align-items: stretch;
    border-bottom: 1px solid #f3f4f6;
    &:last-child { border-bottom: none; }
    @media (max-width: 700px) {
        grid-template-columns: 72px 1fr 54px;
        & > *:last-child { grid-column: 2 / -1; }
    }
`;

const StepCell = styled.div`
    padding: 9px 14px;
    font-size: 0.8rem;
    word-break: break-all;
    overflow-wrap: anywhere;
    border-right: 1px solid #f3f4f6;
    &:last-child { border-right: none; }
    display: flex;
    align-items: center;
`;

const StepLabel = styled.span`
    font-size: 0.65rem;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const HopList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const HopLine = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.82rem;
`;

const HopNum = styled.span`
    font-size: 0.68rem;
    font-weight: 700;
    color: #9ca3af;
    min-width: 20px;
`;

/* ── Pagination ───────────────────────────────────────────── */
const PagRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px 0;
`;

const PageBtn = styled.button`
    appearance: none;
    border: 1px solid ${({ $active }) => $active ? "#6366f1" : "#e5e7eb"};
    background: ${({ $active }) => $active ? "#6366f1" : "#fff"};
    color: ${({ $active }) => $active ? "#fff" : "#374151"};
    border-radius: 8px;
    min-width: 36px;
    height: 36px;
    padding: 0 10px;
    font-size: 0.85rem;
    font-weight: ${({ $active }) => $active ? "700" : "500"};
    cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
    opacity: ${({ disabled }) => disabled ? 0.4 : 1};
    transition: all 120ms ease;
    &:hover:not(:disabled) { background: #f5f3ff; border-color: #c7d2fe; color: #4f46e5; }
`;

/* ── Helpers ──────────────────────────────────────────────── */
function hopStyle(code) {
    if (!code) return { bg: "#f3f4f6", color: "#6b7280" };
    if (code < 300)  return { bg: "#dcfce7", color: "#15803d" };
    if (code < 400)  return { bg: "#dbeafe", color: "#1d4ed8" };
    if (code < 500)  return { bg: "#fee2e2", color: "#dc2626" };
    return { bg: "#fce7f3", color: "#9d174d" };
}

function analyseChain(row) {
    const uri = row.uri;
    const chain = row.currentValue?.redirectChainElements || [];
    if (chain.length === 0) return null;

    // The last element in the chain is the final response (200 or error).
    // Redirect hops are everything except the last element.
    const redirectHops = chain.slice(0, -1);
    const finalElement = chain[chain.length - 1];
    const hops = redirectHops.length; // number of actual redirects
    const finalDest = finalElement?.destinationURI || "";
    const finalStatus = finalElement?.httpStatus;

    if (hops === 0) return null; // no actual redirects, just a 200

    // Loop: backend crawlStatus signals it, or final status is still 3xx (never resolved)
    const crawlStatus = (row.currentValue?.crawlStatus || "").toUpperCase();
    const isLoop = crawlStatus.includes("LOOP") || (finalStatus >= 300 && finalStatus < 400);

    // External: use crawlScope set by the backend
    const isExternal = (row.currentValue?.crawlScope || "").toUpperCase() === "EXTERNAL";

    // Broken: final status is 4xx/5xx
    const isBroken = finalStatus >= 400;

    // Long: 2+ redirect hops (i.e. chain of 3+ including final response)
    const isLong = hops >= 2;

    return { hops, finalDest, finalStatus, isLoop, isExternal, isBroken, isLong, chain };
}

function IssueChips({ analysis }) {
    if (!analysis) return null;
    const chips = [];
    if (analysis.isLoop)     chips.push({ label: "Loop",       bg: "#fee2e2", color: "#dc2626" });
    if (analysis.isBroken)   chips.push({ label: "Broken",     bg: "#fce7f3", color: "#9d174d" });
    if (analysis.isExternal) chips.push({ label: "External",   bg: "#fffbeb", color: "#d97706" });
    if (analysis.isLong)     chips.push({ label: "Long chain", bg: "#f5f3ff", color: "#7c3aed" });
    if (chips.length === 0)  chips.push({ label: "OK",         bg: "#dcfce7", color: "#15803d" });
    return (
        <>
            {chips.map(c => (
                <IssueBadge key={c.label} $bg={c.bg} $color={c.color}>{c.label}</IssueBadge>
            ))}
        </>
    );
}

function ChainViz({ chain }) {
    if (!chain || chain.length === 0) return null;
    return (
        <ChainRow>
            {chain.map((hop, i) => {
                const { bg, color } = hopStyle(hop.httpStatus);
                const isLast = i === chain.length - 1;
                return (
                    <React.Fragment key={i}>
                        {i > 0 && <Arrow>→</Arrow>}
                        <Tooltip title={isLast ? hop.destinationURI || "" : ""} placement="top">
                            <HopBadge $bg={bg} $color={color}>{hop.httpStatus}</HopBadge>
                        </Tooltip>
                    </React.Fragment>
                );
            })}
        </ChainRow>
    );
}

function ExpandedDetail({ row, analysis }) {
    const steps = analysis.chain.map((hop, i) => ({
        source: i === 0 ? row.uri : analysis.chain[i - 1].destinationURI,
        status: hop.httpStatus,
        dest: hop.destinationURI,
        isFinal: i === analysis.chain.length - 1,
    }));

    return (
        <DetailTd colSpan={6}>
            <ChainCard>
                {/* Header */}
                <StepRow style={{ background: "#f9fafb" }}>
                    <StepCell><StepLabel>#</StepLabel></StepCell>
                    <StepCell><StepLabel>Source</StepLabel></StepCell>
                    <StepCell style={{ textAlign: "center" }}><StepLabel>Status</StepLabel></StepCell>
                    <StepCell><StepLabel>Destination</StepLabel></StepCell>
                </StepRow>
                {steps.map((step, i) => {
                    const { bg, color } = hopStyle(step.status);
                    return (
                        <StepRow key={i} style={{ background: step.isFinal ? "#f0fdf4" : "#fff" }}>
                            <StepCell style={{ color: "#9ca3af", fontWeight: 700, fontSize: "0.72rem" }}>
                                {step.isFinal ? "✓ Final" : `Hop ${i + 1}`}
                            </StepCell>
                            <StepCell style={{ color: "#4f46e5", fontWeight: 500 }}>
                                {step.source}
                            </StepCell>
                            <StepCell style={{ justifyContent: "center" }}>
                                <HopBadge $bg={bg} $color={color}>{step.status}</HopBadge>
                            </StepCell>
                            <StepCell style={{ color: step.isFinal ? "#16a34a" : "#374151", fontWeight: step.isFinal ? 600 : 400 }}>
                                {step.dest}
                            </StepCell>
                        </StepRow>
                    );
                })}
            </ChainCard>
        </DetailTd>
    );
}

const FILTERS = [
    { key: "all",      label: "All" },
    { key: "loop",     label: "Loops" },
    { key: "long",     label: "Long chains (3+)" },
    { key: "external", label: "External" },
    { key: "broken",   label: "Broken" },
];

const PAGE_SIZE = 50;

export default function RedirectsPage() {
    const { workspaceNumber } = useParams();
    const ws = useSelector((s) => s.workspaces?.current);

    const [rows, setRows] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState("all");
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        setLoading(true);

        const base = `/api/monitored-uris/search/searchMonitoredUri`;

        const fetchRedirects = apiFetch(`${base}?${new URLSearchParams({
            workspaceNumber,
            httpStatus: "300,301,302,303,304,307,308",
            page,
            size: PAGE_SIZE,
        })}`).then(r => r.json());

        const fetchExternal = apiFetch(`${base}?${new URLSearchParams({
            workspaceNumber,
            crawlScope: "external",
            page: 0,
            size: PAGE_SIZE,
        })}`).then(r => r.json());

        Promise.all([fetchRedirects, fetchExternal])
            .then(([redirectJson, externalJson]) => {
                const redirectData = redirectJson._embedded?.monitoredUris || [];
                const externalData = externalJson._embedded?.monitoredUris || [];
                // Merge, dedup by id
                const seen = new Set(redirectData.map(r => r.id));
                const merged = [...redirectData, ...externalData.filter(r => !seen.has(r.id))];
                setRows(merged);
                setTotalElements(redirectJson.page?.totalElements || 0);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [workspaceNumber, page]);

    const analysed = useMemo(() =>
        rows.map(row => ({ row, analysis: analyseChain(row) })).filter(({ analysis }) => analysis),
        [rows]
    );

    const filtered = useMemo(() => {
        if (activeFilter === "all") return analysed;
        return analysed.filter(({ analysis }) => {
            if (activeFilter === "loop")     return analysis.isLoop;
            if (activeFilter === "long")     return analysis.isLong;
            if (activeFilter === "external") return analysis.isExternal;
            if (activeFilter === "broken")   return analysis.isBroken;
            return true;
        });
    }, [analysed, activeFilter]);

    // Summary counts (current page)
    const loops     = analysed.filter(({ analysis }) => analysis.isLoop).length;
    const multiHop  = analysed.filter(({ analysis }) => analysis.hops >= 2).length;
    const external  = analysed.filter(({ analysis }) => analysis.isExternal).length;
    const broken    = analysed.filter(({ analysis }) => analysis.isBroken).length;

    const totalPages = Math.ceil(totalElements / PAGE_SIZE);

    return (
        <Page>
            {ws && <WorkspaceHeader name={ws.name} url={ws.websiteUrl} countryCode={ws.countryCode} />}

            <Section>
                <TilesRow>
                    <Tile $gradient="linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)" $shadow="rgba(37,99,235,.25)">
                        <TileLabel>Total redirects</TileLabel>
                        <TileNumber>{loading ? "—" : totalElements.toLocaleString()}</TileNumber>
                        <TileSub>on this page: {analysed.length}</TileSub>
                    </Tile>
                    <Tile $gradient="linear-gradient(135deg, #c084fc 0%, #7c3aed 100%)" $shadow="rgba(124,58,237,.25)">
                        <TileLabel>Multi-hop chains</TileLabel>
                        <TileNumber>{loading ? "—" : multiHop}</TileNumber>
                        <TileSub>2 or more hops</TileSub>
                    </Tile>
                    <Tile $gradient="linear-gradient(135deg, #f87171 0%, #e11d48 100%)" $shadow="rgba(225,29,72,.25)">
                        <TileLabel>Redirect loops</TileLabel>
                        <TileNumber>{loading ? "—" : loops}</TileNumber>
                        <TileSub>circular redirects</TileSub>
                    </Tile>
                    <Tile $gradient="linear-gradient(135deg, #fbbf24 0%, #d97706 100%)" $shadow="rgba(217,119,6,.25)">
                        <TileLabel>External redirects</TileLabel>
                        <TileNumber>{loading ? "—" : external}</TileNumber>
                        <TileSub>leaving the domain</TileSub>
                    </Tile>
                </TilesRow>
            </Section>

            <Section>
                <FilterBar>
                    {FILTERS.map(f => (
                        <FilterBtn
                            key={f.key}
                            $active={activeFilter === f.key}
                            onClick={() => setActiveFilter(f.key)}
                        >
                            {f.label}
                        </FilterBtn>
                    ))}
                    <Typography variant="caption" style={{ color: "#9ca3af", marginLeft: 8 }}>
                        {filtered.length} redirect{filtered.length !== 1 ? "s" : ""}
                    </Typography>
                </FilterBar>

                <TableWrap>
                    <StyledTable>
                        <THead>
                            <tr>
                                <Th style={{ width: "32%" }}>Source URL</Th>
                                <Th style={{ width: "16%" }}>Chain</Th>
                                <Th style={{ width: "6%" }}>Hops</Th>
                                <Th style={{ width: "26%" }}>Final destination</Th>
                                <Th style={{ width: "12%" }}>Issues</Th>
                                <Th style={{ width: "8%" }} />
                            </tr>
                        </THead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <Td colSpan={5} style={{ textAlign: "center", color: "#9ca3af", padding: "32px" }}>
                                        Loading…
                                    </Td>
                                </tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr>
                                    <Td colSpan={5} style={{ textAlign: "center", color: "#9ca3af", padding: "32px" }}>
                                        No redirects found
                                    </Td>
                                </tr>
                            )}
                            {!loading && filtered.map(({ row, analysis }) => {
                                const isExpanded = expandedId === row.id;
                                return (
                                <React.Fragment key={row.id}>
                                    <Tr>
                                        <Td style={{ width: "32%" }}>
                                            <Tooltip title={row.uri} placement="top-start">
                                                <UrlLink href={"/#/monitored-uris/" + row.id}>
                                                    {row.uri}
                                                </UrlLink>
                                            </Tooltip>
                                        </Td>
                                        <Td style={{ width: "16%" }}>
                                            <ChainViz chain={analysis.chain} />
                                        </Td>
                                        <Td style={{ width: "6%", fontWeight: 700, color: analysis.isLong ? "#7c3aed" : "#374151" }}>
                                            {analysis.hops}
                                        </Td>
                                        <Td style={{ width: "26%" }}>
                                            <Tooltip title={analysis.finalDest} placement="top-start">
                                                <SubText>{analysis.finalDest || "—"}</SubText>
                                            </Tooltip>
                                        </Td>
                                        <Td style={{ width: "12%" }}>
                                            <IssueChips analysis={analysis} />
                                        </Td>
                                        <Td style={{ width: "8%", textAlign: "right" }}>
                                            <ExpandBtn onClick={() => setExpandedId(isExpanded ? null : row.id)}>
                                                {isExpanded ? "▲ Hide" : "▼ Hops"}
                                            </ExpandBtn>
                                        </Td>
                                    </Tr>
                                    {isExpanded && (
                                        <DetailRow>
                                            <ExpandedDetail row={row} analysis={analysis} />
                                        </DetailRow>
                                    )}
                                </React.Fragment>
                                );
                            })}
                        </tbody>
                    </StyledTable>
                </TableWrap>

                {totalPages > 1 && (
                    <PagRow>
                        <PageBtn disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹</PageBtn>
                        {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => (
                            <PageBtn key={i} $active={i === page} onClick={() => setPage(i)}>{i + 1}</PageBtn>
                        ))}
                        <PageBtn disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>›</PageBtn>
                        <Typography variant="caption" style={{ color: "#9ca3af", marginLeft: 8 }}>
                            Page {page + 1} of {totalPages}
                        </Typography>
                    </PagRow>
                )}
            </Section>
        </Page>
    );
}
